import { getSubscriptionStartDiscountFromSubscription } from './utils';
import { AmountObject } from 'common/types';
import { getSubscriptionPrice, PurchasedPlan } from 'common/modules/plans';
import { BillingCreditsBalance, BILLING_CURRENCY } from 'common/modules/billing';
import { ShopReadOnlyData } from 'common/types';
import { LocationSubscription } from 'common/modules/plans/subscriptions';
import { getCreditsToUseAndBalanceAfter } from 'common/modules/billing/utils/credits';
import { multiplyPrice, sumPrices } from 'common/utils/pricing';
import moment from 'moment';

export interface AddStorePriceBreakdownArgs {
	creditsBalance: BillingCreditsBalance;
	shopReadOnly: ShopReadOnlyData;
	plan: PurchasedPlan;
	newSubscription: LocationSubscription;
	date: moment.Moment;
}

export interface AddStorePriceBreakdown {
	subscriptionPrice: AmountObject;
	startDiscount: AmountObject;
	revenueDiscount: AmountObject;
	leftToPay: AmountObject;
	refundableAmount: AmountObject;
	creditsToUse: {
		refund: number;
		manual: number;
		total: number;
	};
	creditsBalance: BillingCreditsBalance;
}

export const getAddStoreSubscriptionPriceBreakdown = (
	args: AddStorePriceBreakdownArgs,
): AddStorePriceBreakdown => {
	const { creditsBalance, plan, newSubscription, date } = args;

	const subscriptionPrice = getSubscriptionPrice(plan, newSubscription)?.fixed ?? {
		currency: BILLING_CURRENCY.code,
		value: 0,
	};

	const startDiscount = multiplyPrice(
		getSubscriptionStartDiscountFromSubscription({
			plan,
			subscription: newSubscription,
			startDate: date,
		}),
		0.5,
	);

	const revenueDiscount = multiplyPrice(subscriptionPrice, 0.5);

	const priceAfterDiscounts = sumPrices([
		subscriptionPrice,
		multiplyPrice(startDiscount, -1),
		multiplyPrice(revenueDiscount, -1),
	]);

	const { creditsToUse, balanceAfter } = getCreditsToUseAndBalanceAfter({
		creditsBalance: creditsBalance,
		creditsToUse: priceAfterDiscounts.value,
		type: 'any',
	});

	const leftToPay = sumPrices([
		priceAfterDiscounts,
		multiplyPrice(
			{
				value: creditsToUse.total,
				currency: BILLING_CURRENCY.code,
			},
			-1,
		),
	]);

	const refundableAmount = sumPrices([
		leftToPay,
		{
			value: creditsToUse.refund,
			currency: BILLING_CURRENCY.code,
		},
	]);

	return {
		subscriptionPrice,
		startDiscount,
		revenueDiscount,
		leftToPay,
		refundableAmount,
		creditsToUse,
		creditsBalance: balanceAfter,
	};
};
